import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { logErr } from "../lib/utils-trel";
import { Award, Copy, Check, Sparkles, Users, Trophy, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const NAVY = "#071A2D";
const EMERALD = "#064E3B";
const GOLD = "#C9A45C";
const IVORY = "#FFF8EE";

/**
 * ConciergeAwards
 * "My TREL Awards" panel — badges + referral stats + invite link.
 * Used inside ProviderDashboard.
 */
export default function ConciergeAwards({ provider }) {
  const [awards, setAwards] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/providers/awards");
      setAwards(data);
    } catch (e) { logErr("providers/awards", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const certified = !!(provider?.certification?.completed_at || provider?.trel_certified);
  const referralUrl = awards?.referral?.link
    ? `${window.location.origin}${awards.referral.link}`
    : "";

  const copyLink = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { logErr("clipboard/concierge-invite", e); }
  };

  return (
    <section data-testid="concierge-awards" className="mt-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>My TREL Awards</div>
          <h2 className="font-serif text-2xl sm:text-3xl mt-1" style={{ color: NAVY }}>Earn recognition. Grow your network.</h2>
        </div>
      </div>

      {/* Certification gate — show only if not yet certified */}
      {!certified && (
        <div
          className="mt-5 rounded-3xl p-5 sm:p-6 border flex items-start gap-4"
          style={{ background: `${GOLD}10`, borderColor: `${GOLD}55` }}
          data-testid="concierge-awards-cert-cta"
        >
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${GOLD}22`, color: "#8a6a2e" }}>
            <Award size={20}/>
          </div>
          <div className="flex-1">
            <div className="font-medium" style={{ color: NAVY }}>Complete TREL Certified Training to unlock awards.</div>
            <div className="text-sm mt-1" style={{ color: "rgba(7,26,45,0.65)" }}>
              The 5-page training takes ~10 minutes and activates your concierge profile.
            </div>
          </div>
          <Link to="/onboarding/concierge" className="btn-coral text-sm inline-flex items-center gap-1.5">
            Start training <ChevronRight size={14}/>
          </Link>
        </div>
      )}

      {/* Stats strip */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Completed experiences" value={awards?.stats?.completed_experiences ?? 0}/>
        <StatCard label="Average rating" value={(awards?.stats?.average_rating ?? 0).toFixed(2)}/>
        <StatCard label="Successful referrals" value={awards?.stats?.successful_referrals ?? 0}/>
        <StatCard label="Total invites" value={awards?.stats?.total_referrals ?? 0}/>
      </div>

      {/* Badges grid */}
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(awards?.badges || []).map((b) => (
          <BadgeCard key={b.key} badge={b}/>
        ))}
      </div>

      {/* Referral link */}
      <div
        className="mt-6 rounded-3xl p-5 sm:p-7 border bg-white"
        style={{ borderColor: "rgba(7,26,45,0.10)" }}
        data-testid="concierge-referral-card"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${EMERALD}10`, color: EMERALD }}>
            <Users size={20}/>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-medium" style={{ color: NAVY }}>Invite a fellow concierge</div>
            <div className="text-sm mt-1" style={{ color: "rgba(7,26,45,0.65)" }}>
              When they complete certification, you progress toward the <b>TREL Ambassador</b> award.
            </div>
            {awards?.referral?.code && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <code className="px-3 py-1.5 rounded-lg text-xs font-mono bg-[#FFF8EE] border" style={{ borderColor: "rgba(7,26,45,0.10)", color: NAVY }} data-testid="concierge-referral-code">
                  {awards.referral.code}
                </code>
                <button
                  onClick={copyLink}
                  className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hover:bg-[#FFF8EE]"
                  style={{ borderColor: "rgba(7,26,45,0.10)", color: NAVY }}
                  data-testid="concierge-referral-copy"
                >
                  {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy invite link</>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl bg-white border px-4 py-3" style={{ borderColor: "rgba(7,26,45,0.08)" }}>
    <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "rgba(7,26,45,0.55)" }}>{label}</div>
    <div className="font-serif text-2xl mt-1" style={{ color: NAVY }}>{value}</div>
  </div>
);

function BadgeCard({ badge }) {
  const achieved = !!badge.achieved;
  const hasProgress = typeof badge.progress === "number" && typeof badge.target === "number" && badge.target > 0;
  const pct = hasProgress ? Math.min(100, Math.round((badge.progress / badge.target) * 100)) : (achieved ? 100 : 0);

  return (
    <article
      className="rounded-2xl p-5 border transition-all"
      style={{
        background: achieved ? `${GOLD}10` : "#fff",
        borderColor: achieved ? `${GOLD}66` : "rgba(7,26,45,0.10)",
      }}
      data-testid={`badge-${badge.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: achieved ? `${GOLD}22` : IVORY, color: achieved ? "#8a6a2e" : "rgba(7,26,45,0.45)" }}>
          {achieved ? <Trophy size={18}/> : <Sparkles size={18}/>}
        </div>
        {achieved && (
          <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full" style={{ background: `${GOLD}22`, color: "#8a6a2e" }}>
            Earned
          </span>
        )}
      </div>
      <div className="mt-3 font-medium" style={{ color: NAVY }}>{badge.name}</div>
      <div className="text-xs mt-1" style={{ color: "rgba(7,26,45,0.6)" }}>{badge.description}</div>
      {hasProgress && (
        <>
          <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(7,26,45,0.08)" }}>
            <div className="h-full" style={{ width: `${pct}%`, background: achieved ? GOLD : EMERALD }}/>
          </div>
          <div className="mt-1 text-[11px]" style={{ color: "rgba(7,26,45,0.55)" }}>
            {badge.progress} / {badge.target}
          </div>
        </>
      )}
    </article>
  );
}
